/** The adventure screen, wired.
 *
 * The canvas is a sibling of the panel layer; the store is the app's session;
 * every overlay is a leaf that takes plain props and returns commands. Nothing
 * here holds simulation state - `useSyncExternalStore` re-reads the session on
 * its version and that is the whole subscription model.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSyncExternalStore } from "react";
import { flushSync } from "react-dom";
import type {
  ArmyId, ArmySlot, ArmyState, ArtifactDefId, BattleCommand, BattleState, BuildingDefId, ContentRegistry, CreatureDefId,
  GameCommand, GameEvent, GameState, HeroDefId, HeroId, HeroState, ObjectId, PlayerId, PlayerObservation,
  Reason, ResourceKind, SpellDefId, TownId, TownState,
} from "@game/core";
import {
  SIDE_ATTACKER, SIDE_DEFENDER, adventureTargeting, availableOf, entityKeys, makeCtx, maxMana,
  payloadOf, primaryFor, reasonText, townUpgradeOffers, tradeYield,
} from "@game/core";
import type { Atlas as UiAtlas } from "@game/ui";
import {
  AdventureScreen, AtlasContext, DEFAULT_MAP_ZOOM, GameStoreContext, InfoPopup, NO_HOVER, NO_SELECTION,
  centreCamera, clampCamera, describeInfo, findHero, hasInfo, infoTargetAt, isPhoneLandscape,
  stepMapZoom, toggleLevel,
  type CameraView, type GameStore, type HoverTarget, type InfoCard, type MapExtent,
  type Selection, type StatusState, type TouchCell,
} from "@game/ui";
import {
  ArtifactMerchant, BattleResult, GameOver, HeroScreen, Journal, KingdomOverview,
  PuzzleMap,
  SpellBook, TownScreen,
} from "@game/ui";
import { heroPortrait, titleCase } from "@game/ui";
import type {
  ArtifactOffer, BattleReport, CasualtyLine, GameOutcome, SideReport, StandingRow,
} from "@game/ui";
import type { AtlasLike } from "@game/render";
import type { AdventurePresenter, BattlePresenter } from "@game/render";
import { PromptDialog } from "./PromptDialog.js";
import { WorldCanvas, applyCamera } from "./world/WorldCanvas.js";
import type { Aiming } from "./world/aiming.js";
import { aimingPrompt, cellIndexOf } from "./world/aiming.js";
import { BattleScreen } from "./battle/BattleScreen.js";
import { app } from "./game/app.js";
import type { Overlay } from "./game/app.js";
import { journalChronicle, journalQuests } from "./game/journal.js";
import type { GameSession } from "./game/store.js";

export interface AdventureShellProps {
  readonly session: GameSession;
  readonly uiAtlas: UiAtlas;
  readonly pixiAtlas: AtlasLike | null;
  readonly animations: boolean;
  /** Screenshot harness: pin the panel scale so two runs are comparable. */
  readonly uiScale?: number | undefined;
  /** Opens the app's system panel. The key that fires it is drawn by the
   *  adventure screen, in the resource bar, not floated over the map. */
  readonly onOpenMenu?: (() => void) | undefined;
}

/** Longest the turn loop will hold for a battle animation; see `app.present`. */
const ANIM_WAIT_CAP_MS = 6000;

function storeFor(session: GameSession): GameStore {
  return {
    subscribe: (l) => session.subscribe(l),
    getVersion: () => session.getVersion(),
    getObservation: () => session.getObservation(),
    getContent: () => session.getContent(),
    dispatch: (c: GameCommand) => { app.dispatch(c); },
    check: (c: GameCommand) => session.check(c),
  };
}

export function AdventureShell(props: AdventureShellProps): JSX.Element {
  const { session } = props;
  const version = useSyncExternalStore(
    useCallback((l: () => void) => session.subscribe(l), [session]),
    useCallback(() => session.getVersion(), [session]),
  );
  const snapshot = useSyncExternalStore(app.subscribe, app.getSnapshot);

  const store = useMemo(() => storeFor(session), [session]);
  const state = session.getState();
  const obs = session.getObservation();
  const content = session.getContent();

  // The map's own magnification, apart from the browser's: `-` and `=` (or
  // `+`) step it through `MAP_ZOOM_LEVELS`, and the canvas turns it into a
  // viewport of more or fewer cells around the same centre. It is view
  // state like the camera, not a preference: a new game opens at 1.
  const [mapZoom, setMapZoom] = useState<number>(() => (
    typeof window !== "undefined" && isPhoneLandscape(window.innerWidth, window.innerHeight)
      ? 0.75
      : DEFAULT_MAP_ZOOM
  ));
  const [camera, setCamera] = useState<CameraView>(
    () => ({ x: 0, y: 0, z: 0, widthCells: 20, heightCells: 12 }),
  );
  const [selection, setSelection] = useState<Selection>(NO_SELECTION);
  const [hover, setHover] = useState<HoverTarget>(NO_HOVER);
  /**
   * The right-press info card, and where the press was.
   *
   * Held as the resolved card rather than as the target, because the card is
   * built from the observation at the moment of the press: a card that
   * re-derived itself on every render would change under the player's hand
   * while the computer moved behind it, which is the one thing a "what is this"
   * panel must not do.
   */
  const [infoCard, setInfoCard] = useState<
    { readonly card: InfoCard; readonly x: number; readonly y: number } | null
  >(null);
  const presenterRef = useRef<AdventurePresenter | null>(null);

  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  /** A refusal the shell decided not to issue, and the version it was true at. */
  const [notice, setNotice] = useState<{ at: number; reason: Reason } | null>(null);

  /**
   * The adventure spell the player has armed a cursor with, or null.
   *
   * Three of the ten map spells take a target the caster picks and the shell had
   * no way to pick one, so it passed the cell the hero was standing on for all
   * ten. That is a legal cast for seven of them and for none of the three:
   * Blink Step is refused at distance zero, Sink Vessel may not sink the
   * hull its caster is in, and Homeward Gate at expert silently fell back to the
   * nearest town because the cell it was handed named no town at all. The
   * cursor is what closes that; see `world/aiming.ts`.
   */
  const [aiming, setAiming] = useState<Aiming | null>(null);
  const aimingRef = useRef<Aiming | null>(null);
  aimingRef.current = aiming;

  // ---- who the map is pointed at
  //
  // This used to latch: the first hero the player owned was selected once and
  // the effect never ran again. Heroes could not die then. Now they can, and a
  // selection pointing at a hero who is off the map is worse than none - the
  // map click reader is handed `null` for the hero and every click on the world
  // becomes a silent no-op with nothing on screen to say why. So the selection
  // is RECONCILED against the observation instead of latched: a hero who is
  // still there is left alone, and one who is gone is replaced by whoever is
  // left. The same for the town, which a player can lose.
  useEffect(() => {
    const self = obs.self?.id ?? null;
    const cur = selectionRef.current;
    const heroAlive = cur.heroId !== null
      && obs.heroes.some((h) => h.id === cur.heroId && h.owner === self);
    const townHeld = cur.townId !== null
      && obs.towns.some((t) => t.id === cur.townId && t.owner === self);
    if (heroAlive && townHeld) return;
    const heroId = heroAlive ? cur.heroId
      : obs.heroes.find((h) => h.owner === self)?.id ?? null;
    const townId = townHeld ? cur.townId
      : obs.towns.find((t) => t.owner === self)?.id ?? null;
    if (heroId === cur.heroId && townId === cur.townId) return;
    setSelection({ heroId, townId });
  }, [obs]);

  // ---- the camera policy
  //
  // The camera used to be driven by an effect keyed on the observation that
  // re-centred whenever the selected hero was outside the visible rect. That
  // fires on EVERY observation change - an enemy's move, a fog reveal, a
  // resource tick - and "the hero is off screen" is not an error to correct, it
  // is what panning away FROM him looks like. So the map snapped back and the
  // player could not pan at all.
  //
  // The policy that replaces it: the camera moves only when there is a reason
  // to move it, and a pan is sticky because nothing takes it back.
  //
  //   - the selection changed         -> centre on it
  //   - the viewport was measured     -> bring the selection back into view
  //   - the player's own hero walked  -> follow the walk, and stay where it ended
  //   - anything else                 -> leave the camera alone
  //
  // Everything else that moves the camera is the player's own hand: the drag
  // pan and the edge scroll in `WorldCanvas`, the arrow keys and `c` in
  // `AdventureScreen`, and the minimap.
  const obsRef = useRef(obs);
  obsRef.current = obs;
  const cameraRef = useRef(camera);
  cameraRef.current = camera;
  const mapRef = useRef<MapExtent>({
    width: obs.map.width, height: obs.map.height, levels: obs.map.levels,
  });
  mapRef.current = { width: obs.map.width, height: obs.map.height, levels: obs.map.levels };

  /**
   * Every camera the player's own hand produces goes through here.
   *
   * Clamping in one place rather than at each source is the point: the arrow
   * keys and the drag pan clamp themselves, and the minimap does not - a click
   * near the border of the map image sets a top-left cell outside the map, the
   * renderer clamps its own copy, and the two then disagree by however far
   * outside it landed, so the camera outline on the minimap sits somewhere the
   * map is not and the next drag spends itself unwinding the difference.
   */
  const panCamera = useCallback((c: CameraView): void => {
    setCamera(clampCamera(c, mapRef.current));
  }, []);

  /** Put a cell on screen. `force` centres on it even if it is already there. */
  const showCell = useCallback((x: number, y: number, z: number, force: boolean): void => {
    setCamera((c) => (
      !force && cellInView(c, x, y, z) ? c : centreCamera(c, mapRef.current, x, y, z)
    ));
  }, []);

  /** Centre on a cell the player picked out of a list, and change plane if the
   *  cell is on the other one - a journal entry underground is otherwise a
   *  camera move to a place the player cannot see. */
  const goTo = useCallback((x: number, y: number, z: number): void => {
    showCell(x, y, z, true);
  }, [showCell]);

  // Centre when the SELECTION changes; when only the viewport changed - which
  // includes the first measurement, since the camera opens at a guessed 20x12 -
  // just make sure the selection is still on screen.
  //
  // The SESSION is in the deps for a reason that cost a screenshot round. This
  // component is not remounted between two games - `App` renders it without a
  // key - so the camera, and this ref, survive `newGame`. Two games generated
  // from two seeds hand their first hero the same id, so on the second game
  // `shownHero.current !== id` was false, the effect never re-ran, and the
  // camera stayed pointed at wherever the previous world's hero had been. On
  // the night scene that is 99.4% empty shroud with the hero and the town off
  // screen entirely, which reads as a failed load. A new session is a new
  // world: the camera is aimed at the selection from scratch, exactly as it is
  // on the first.
  const shownHero = useRef<HeroId | null>(null);
  const shownSession = useRef<GameSession | null>(null);
  useEffect(() => {
    const id = selection.heroId;
    if (id === null) return;
    const hero = obsRef.current.heroes.find((h) => h.id === id);
    if (hero === undefined) return;
    const changed = shownSession.current !== session || shownHero.current !== id;
    shownSession.current = session;
    shownHero.current = id;
    showCell(hero.x, hero.y, hero.z, changed);
  }, [session, selection.heroId, camera.widthCells, camera.heightCells, showCell]);

  /**
   * A hero the player has just ordered somewhere, and where he stood when the
   * order went in.
   *
   * The camera follows a move only when the player asked for it and only while
   * it is running. The starting cell is kept so that a command the kernel
   * refused - which produces no events at all, so nothing ever clears this -
   * cannot make the next computer turn look like the player's own move.
   *
   * It is set by every command that CAN move him, not only by `MOVE_HERO`.
   * `VISIT_OBJECT` is the one that matters and the one that was missing: a
   * hero standing beside a subterranean gate with no movement left reaches out
   * to it instead of stepping onto it (`readMapClick` -> `VISIT`), the kernel
   * teleports him to the other level, and with nothing recorded here the map
   * stayed on the level he had just left - the hero gone from the board, the
   * camera looking at where he used to be.
   */
  const walking = useRef<{ hero: HeroId; x: number; y: number; z: number } | null>(null);

  const noteOrder = useCallback((hero: HeroId): void => {
    const h = obsRef.current.heroes.find((x) => x.id === hero);
    walking.current = h === undefined ? null : { hero, x: h.x, y: h.y, z: h.z };
  }, []);

  const moveHero = useCallback((hero: HeroId, path: readonly number[]): void => {
    noteOrder(hero);
    app.dispatch({ type: "MOVE_HERO", hero, path });
  }, [noteOrder]);

  // ---- the after-action report
  //
  // A battle the player fought and then never heard about again was the whole
  // of the feedback here: `BattleResult` and `battleReport.ts` were finished and
  // nothing set the `{kind:"result"}` overlay they exist for. Wiring it needs one
  // awkward fact stated plainly, because it decides the shape of everything
  // below.
  //
  // **The final `BattleState` is not observable from outside the kernel.**
  // `resolveBattleOutcome` folds the survivors back into the adventure armies
  // and sets `c.s.battle = null` inside the SAME command that lands the last
  // blow, so no host can ever hold the battle as it ended - the state before
  // that command is one blow stale and the state after it has no battle at all.
  // `buildBattleReport` takes a `BattleState` and therefore cannot be called
  // from here at all; see the note in the final report.
  //
  // What IS exact is the pair of armies. `foldSide` writes the survivors into
  // `state.armies` before the battle is cleared, so "what he began with" is the
  // army snapshotted when the battle opened and "what walked away" is the same
  // army read afterwards. Both are plain state reads: this file counts, it does
  // not decide. The one thing that pair cannot separate is Gravecalling, which
  // raises its skeletons into the winner's army after the fold - so a raised
  // stack shows as more survivors than it began with, and `lost` is floored at
  // zero rather than being allowed to go negative.
  const battleTrack = useRef<BattleTrack | null>(null);
  const resultView = useRef<ResultView | null>(null);
  /**
   * Finished reports waiting behind the one on screen, oldest first.
   *
   * One fight's report used to overwrite the overlay the moment it finished,
   * so when two fights ended back to back - the common case on the computer's
   * turn, when it storms two of the player's stacks, or the player's own two
   * guards clear two wandering packs - the first report was gone before anyone
   * could read it. Entries are appended by `publishReport` and shifted off by
   * the result screen's `onClose`, which is the only reader. A ref rather than
   * state because enqueueing while the modal is up must not re-render anything:
   * the screen keeps showing the current report and the queue is only read on
   * close, which re-renders through the overlay change anyway.
   */
  const reportQueue = useRef<QueuedReport[]>([]);
  /**
   * A new session is a new world: drop anything the previous game left behind.
   * This shell is not remounted between games, so without this a report queued
   * in one game would surface over the next one.
   */
  const reportSession = useRef<GameSession | null>(null);
  if (reportSession.current !== session) {
    reportSession.current = session;
    reportQueue.current = [];
    battleTrack.current = null;
    resultView.current = null;
  }

  /**
   * The kernel's events for the frame the battle screen is about to draw.
   *
   * `BattleScreen` was handed a literal `[]` here, which meant the one thing
   * that could narrate the console - the event stream - never reached it, and
   * the crimson recess had nothing to read but "Round 1". `@game/render` never
   * sees a `GameEvent` by design (I10), so the app is the only place these can
   * be turned into sentences; see `battle/narrate.ts`.
   *
   * React state rather than a ref because the screen has to re-render when a
   * batch arrives: `app.present` is called from inside the turn loop's await,
   * outside React's world, and a ref would be read on some later unrelated
   * render or not at all.
   */
  const [battleEvents, setBattleEvents] = useState<readonly GameEvent[]>([]);

  /**
   * The mounted battle presenter, so the real game can drive its animator.
   *
   * The whole battle timeline - idle breath, the walk cycle, the attack lunge
   * with the damage landing on the contact frame, the projectile, the death
   * collapse - is played by `BattlePresenter.animate(events)`, and `update()`
   * does NOT play it: they are separate methods and the presenter's own doc
   * comment says so. Until this ref existed the only call site was `App.tsx`'s
   * debug fixture, so a player fighting a real battle saw stacks teleport
   * between hexes while the console narrated what he had not been shown. See
   * `app.present` below for the ordering this ref exists to make possible.
   */
  const battlePresenterRef = useRef<BattlePresenter | null>(null);

  /*
   * There were two refs here - a `WeakSet` of events the animator had already
   * been given, and the play they started - because `app.dispatch` had two
   * doors and only one reached `app.present`. In a battle the turn loop sets
   * `onActor(null, "neutral")` before the computer's stack acts and does not
   * name the human again until after it has awaited `present`, while the kernel
   * has already given the turn back; an order arriving in that window ran
   * `session.execute` directly, so its events reached the screen only on the
   * NEXT batch, by which time React had parked the stack on its destination and
   * the walk was a teleport. This file played those events itself and used the
   * `WeakSet` so `present` would not play them twice.
   *
   * `app.dispatch` now asks the KERNEL who may act rather than the loop's
   * mirror, so the window no longer routes anything down the second door -
   * measured at 0 of 43 dispatches across four runs, against 1-2 of 10 before.
   * The workaround is gone; `apps/web/test/dispatch-routing.spec.ts` is what
   * keeps it unnecessary, and it fails against the old `const seat = this.human`.
   */

  /** A battle order from the board or the console. The seat parks it and the
   *  turn loop runs it, which is what paces the battle to its animation. */
  const battleCommand = useCallback((cmd: BattleCommand): void => {
    app.dispatch({ type: "BATTLE", cmd });
  }, []);

  /**
   * Publish a finished report: show it, or queue it behind the one on screen.
   *
   * The overlay is read live rather than from the render-scope `snapshot`,
   * which is stale inside this callback by the time the next battle ends: two
   * fights finishing back to back - the computer storming two stacks on its
   * turn, or two of the player's guards clearing two packs - used to have the
   * second `setOverlay` overwrite the first before anyone could read it.
   */
  const publishReport = useCallback((report: BattleReport, view: ResultView): void => {
    if (app.getSnapshot().overlay.kind === "result") {
      reportQueue.current.push({ report, view });
      return;
    }
    resultView.current = view;
    app.setOverlay({ kind: "result", report });
  }, []);

  /**
   * Dismiss the report on screen and show the next queued one, if any.
   *
   * Wired to the result overlay's `onClose`; every other overlay still closes
   * directly. Presentation-only: nothing here awaits, so the turn loop keeps
   * animating under the modal exactly as before.
   */
  const closeResult = useCallback((): void => {
    const next = reportQueue.current.shift();
    if (next === undefined) {
      resultView.current = null;
      app.closeOverlay();
      return;
    }
    resultView.current = next.view;
    app.setOverlay({ kind: "result", report: next.report });
  }, []);

  const noteBattle = useCallback((events: readonly GameEvent[]): void => {
    const st = session.getState();
    const content = session.getContent();
    if (content === null) return;

    if (st.battle !== null) {
      const side = sideOf(session);
      // Not the viewer's fight. A computer clearing a monster across the map
      // owes the player no report. Still delimit: every fight opens with
      // BattleStarted, so an AI-vs-AI fight drops any stale track here instead
      // of letting it merge the viewer's previous fight into the next one.
      if (side === null) {
        if (events.some((e) => e.type === "BattleStarted")) battleTrack.current = null;
        return;
      }
      const track = battleTrack.current;
      // A BattleStarted without an observed end in between means back-to-back
      // fights, so each fight gets exactly one entry: a stale track that
      // already holds its BattleEnded is published first, and a fresh one
      // opens here either way.
      if (track === null || events.some((e) => e.type === "BattleStarted")) {
        if (track !== null && track.events.some((e) => e.type === "BattleEnded")) {
          publishReport(
            reportFrom(content, st, track),
            {
              viewerSide: track.viewerSide,
              attackerName: track.names[0],
              defenderName: track.names[1],
              portraits: track.portraits,
            },
          );
        }
        battleTrack.current = openTrack(st, content, side, events);
      } else {
        track.rounds = st.battle.round;
        for (const e of events) track.events.push(e);
      }
      setBattleEvents(events);
      return;
    }

    const track = battleTrack.current;
    if (track === null) return;
    battleTrack.current = null;
    for (const e of events) track.events.push(e);
    publishReport(
      reportFrom(content, st, track),
      {
        viewerSide: track.viewerSide,
        attackerName: track.names[0],
        defenderName: track.names[1],
        portraits: track.portraits,
      },
    );
  }, [session, publishReport]);

  // ---- the loop's animation hook
  useEffect(() => {
    app.present = async (events): Promise<void> => {
      // ---- the battle animator, FIRST and SYNCHRONOUSLY.
      //
      // `BattleAnimator` snapshots where every stack in the plan is being
      // *shown* at the instant `animate` is called, and advances from there.
      // That snapshot is only worth anything while the scene still holds the
      // pre-command board, so the call has to happen before anything on this
      // path awaits and before any re-render can reach `BattlePresenter.update`,
      // which parks every stack on its destination hex. Get that order wrong
      // and a walk interpolates destination-to-destination: a teleport followed
      // by six frames of gait on the spot. `animate.ts` carries the long
      // version of this note; it is the defect it was written for.
      //
      // The session defers its listener notification to the next animation
      // frame (`GameSession.bump`), and the turn loop calls this straight after
      // `execute`, so everything up to the first `await` here still runs on the
      // board the player is looking at.
      //
      // `enabled === false` - the screenshot harness - makes `animate` apply
      // every terminal state synchronously and resolve before returning, so
      // this costs a settled frame nothing and never makes a shot wait.
      const battlePresenter = battlePresenterRef.current;
      const parts: Promise<void>[] = [];
      // The fight the SCREEN still has, not the one the kernel has: the last
      // blow arrives in the same batch as `state.battle = null`, and it is the
      // one blow most worth watching. `closing` keeps the screen up while it
      // plays; see the note beside that state.
      const stillFighting = session.getState().battle !== null;
      const showing = stillFighting || lastBattle.current !== null;
      if (battlePresenter !== null && showing && events.length > 0) {
        parts.push(battlePresenter.animate(events));
        // `flushSync`, and not a plain `setState`, for a reason worth stating.
        // `noteBattle` two lines down opens the after-action report through the
        // app model, which every screen reads with `useSyncExternalStore` - and
        // an external-store change re-renders SYNCHRONOUSLY, in a lane that
        // does not carry updates queued from outside React. So the shell
        // re-rendered with `closing` still null, unmounted the battle screen,
        // disposed the presenter mid-plan, and only then committed the hold -
        // which remounted a fresh presenter with nothing to play. Measured:
        // seven frames after the kernel cleared the battle still had a canvas
        // and NONE of them had `presenter.busy` true. Committing the hold
        // before the store moves is what makes it a hold.
        if (!stillFighting) flushSync(() => { setClosing(lastBattle.current); });
      }
      const playing = parts.length === 0 ? null : Promise.race([
        Promise.all(parts).then(() => undefined),
        // A hang here stops the turn loop for the rest of the game, so the
        // wait is capped. Nothing the animator plans runs anywhere near this
        // long (a nine-hex walk is 1.5s); the cap is only there so that a
        // presenter whose ticker has stopped - a disposed canvas, a hidden
        // tab that never comes back - cannot take the game with it. The next
        // `animate` call finishes whatever is still queued.
        new Promise<void>((r) => { setTimeout(r, ANIM_WAIT_CAP_MS); }),
      ]);

      // Before anything can return early: the report is owed whether or not a
      // presenter is mounted.
      noteBattle(events);

      // Two of the player's own heroes met: one was walked into the other and
      // stopped a cell short, which is all the kernel does about it. Without
      // this the walk just ended for no visible reason. Only the viewer's own
      // meetings open a window - the computer's heroes meet each other all
      // day.
      for (const e of events) {
        if (e.type !== "HeroesMet") continue;
        if (session.getState().heroes[e.hero]?.owner !== session.getViewer()) continue;
        app.setOverlay({ kind: "hero", hero: e.hero, with: e.other });
        break;
      }

      const p = presenterRef.current;
      if (p === null) { await playing; setClosing(null); return; }
      p.update({ obs: session.getObservation(), events, version: session.getVersion() });
      let animated = false;
      if (props.animations) {
        const ac = new AbortController();
        await p.animate(events, ac.signal);
        animated = true;
      } else {
        await p.settled();
      }
      // The world's animation and the battle's run on the same clock, so this
      // is the join and not a second wait. The turn loop awaits `present`, so
      // holding here is what paces a battle: the next command - the computer's
      // reply, or the player's own next order - is not issued until the blow
      // the player is watching has landed.
      await playing;
      // The last creature is down; the board may go and the report may come.
      setClosing(null);

      // `animate` points the presenter at the hero it walked and never unpoints
      // it, and the presenter re-centres on whatever it is pointed at on every
      // idle tick - four times a second, forever. That is the other half of
      // "the map snaps back after I move a hero": the pan lands, and 260 ms
      // later the renderer drags it home. Following ends with the walk.
      p.setFollowedHero(null);

      const asked = walking.current;
      walking.current = null;
      const hero = asked === null ? null
        : session.getObservation().heroes.find((h) => h.id === asked.hero) ?? null;
      if (hero !== undefined && hero !== null
        && asked !== null && (hero.x !== asked.x || hero.y !== asked.y || hero.z !== asked.z)) {
        // The player watched his own hero walk, so the view he is left with is
        // the one the walk ended on.
        showCell(hero.x, hero.y, hero.z, animated);
      } else {
        // A computer player moved, or fog lifted, or nothing moved at all. The
        // renderer may have scrolled itself while it animated; put it back on
        // the camera the player chose - INCLUDING its level. A player who has
        // gone to look at the other half of the map is not corrected here: the
        // camera follows his hero when he orders the hero somewhere, and at no
        // other time.
        applyCamera(p, cameraRef.current);
      }
    };
    return () => { app.present = null; };
  }, [session, props.animations, showCell, noteBattle]);

  useEffect(() => {
    const p = presenterRef.current;
    if (p === null) return;
    if (props.animations) p.enableAnimations();
    else p.disableAnimations();
  }, [props.animations, version]);

  // ---- status line
  //
  // `notice` is a refusal the shell worked out with `check` and chose NOT to
  // issue. A command the UI knows the kernel will refuse must not be sent (a
  // UI that issues an illegal command is a legality bug), but a click that
  // does nothing and says nothing is the bug this round was opened for. The
  // Reason is the kernel's own, rendered by the status bar's `reasonText`, and
  // it is stamped with the version it was true at so the next command clears
  // it without anyone having to remember to.
  const status = useMemo<StatusState>(() => {
    const refusal = session.getRefusal();
    const hero = selection.heroId;
    let move: StatusState["move"] = null;
    if (hero !== null && hover.kind === "CELL") {
      const set = session.reachableFor(hero);
      const idx = hover.y * obs.map.width + hover.x + hover.z * obs.map.width * obs.map.height;
      const cost = set === null ? -1 : (set.cost[idx] ?? -1);
      const budget = Math.max(1, session.movementLeft(hero));
      move = cost < 0
        ? { costPoints: 0, turns: 0, reachable: false }
        : { costPoints: cost, turns: Math.max(1, Math.ceil(cost / budget)), reachable: true };
    }
    const spoken = notice !== null && notice.at === version ? notice.reason : null;
    return { hover, reason: spoken ?? refusal?.reason ?? null, move };
  }, [session, selection.heroId, hover, obs.map.width, obs.map.height, version, notice]);

  // A cursor armed by a hero who has since died, or left over into the
  // computer's turn, would be a mode the player cannot get out of by clicking.
  useEffect(() => {
    const aim = aimingRef.current;
    if (aim === null) return;
    const hero = obs.heroes.find((h) => h.id === aim.hero) ?? null;
    if (hero === null || snapshot.actorKind !== "human" || state.victory.over) setAiming(null);
  }, [obs, snapshot.actorKind, state.victory.over]);

  const capacities = useMemo(() => session.capacities(), [session, version]);

  const endTurnReason = useMemo<Reason | null>(() => {
    const self = obs.self;
    if (self === null) return null;
    // `actorKind` is the TURN LOOP's opinion of who is acting and it starts at
    // "none" - so `!== "human"` refused the end-turn key on the opening frame
    // of every game, before the loop had announced anything, while the plate
    // six pixels to its left read "Your turn". The observation is the kernel's
    // own answer to the same question and it is right from the first render;
    // the loop's answer is only consulted where it says somebody ELSE is
    // acting, which is the case the observation cannot see mid-hand-over.
    if (snapshot.actorKind === "ai" || snapshot.actorKind === "neutral") {
      return { code: "NOT_YOUR_TURN" };
    }
    if (!obs.isActive) return { code: "NOT_YOUR_TURN" };
    return session.check({ type: "END_TURN", player: self.id });
  }, [session, obs.self, obs.isActive, snapshot.actorKind, version]);

  const endTurn = useCallback((): void => {
    const self = obs.self;
    if (self !== null) app.dispatch({ type: "END_TURN", player: self.id });
  }, [obs.self]);

  const openTown = useCallback((id: TownId): void => {
    app.setOverlay({ kind: "town", town: id });
  }, []);

  // A click that reached an adjacent map object. A shop opens its counter;
  // anything else is the kernel's own VISIT_OBJECT, which is how a hero
  // triggers a structure whose cells he can never stand on.
  /**
   * Open and close the right-press card.
   *
   * Built here rather than in `WorldCanvas` because the card is the shell's
   * business - the canvas knows what the pointer landed on and nothing about
   * what a player is owed to be told about it - and because the same two
   * callbacks will serve the hero list and the town list when they learn the
   * gesture.
   */
  const showInfo = useCallback((target: HoverTarget, x: number, y: number): void => {
    const card = describeInfo(obsRef.current, session.getContent(), target);
    setInfoCard(hasInfo(card) ? { card, x, y } : null);
  }, [session]);

  const hideInfo = useCallback((): void => { setInfoCard(null); }, []);

  /**
   * The phone's Info key: the card for the armed destination - the cell the
   * last tap drew a path to - or, with no path armed, for what the selected
   * hero is standing on. A thumb has no right button and the long press that
   * stands in for one is a gesture nobody is told about; the key is the same
   * card, one tap, at the centre of the glass so the flip logic keeps it
   * whole. Pressed again, or followed by any tap on the map, it goes away.
   */
  const [pendingCell, setPendingCell] = useState<TouchCell | null>(null);
  const infoOpen = infoCard !== null;
  const infoAtArmed = useCallback((): void => {
    if (infoOpen) { setInfoCard(null); return; }
    const obs = obsRef.current;
    let cell = pendingCell;
    if (cell === null) {
      const hero = findHero(obs, selectionRef.current.heroId);
      if (hero === null) return;
      cell = { x: hero.x, y: hero.y, z: hero.z };
    }
    const target = infoTargetAt(obs, cell.x, cell.y, cell.z);
    showInfo(target, Math.round(window.innerWidth / 2), Math.round(window.innerHeight / 2));
  }, [infoOpen, pendingCell, showInfo]);

  const visitObject = useCallback((id: ObjectId): void => {
    const hero = selectionRef.current.heroId;
    if (hero === null) return;
    if (merchantOffers(session.getState(), session.getContent(), id).length > 0) {
      app.setOverlay({ kind: "merchant", object: id, hero });
      return;
    }
    // A visit can move him: a subterranean gate and a monolith both teleport,
    // and a gate reached from beside it is a visit rather than a walk.
    noteOrder(hero);
    app.dispatch({ type: "VISIT_OBJECT", hero, object: id });
  }, [session, noteOrder]);

  // ---- casting an adventure spell
  //
  // The split is: the KERNEL says which cells are legal (`adventureTargeting`),
  // the SHELL holds the armed cursor and shows the set, and the kernel is asked
  // again about the one cell the player actually clicked. The second question is
  // not redundant - the first answer is a snapshot and the map may have moved
  // under it - and it is the one that produces the Reason a refused click is
  // owed.

  const cancelAim = useCallback((): void => { setAiming(null); }, []);

  /**
   * The spellbook's cast button.
   *
   * A spell that goes off where the hero stands is cast at once, exactly as
   * before. One that takes a target arms the cursor and closes the book instead
   * - and when it has no legal target at all, the player is told why rather than
   * being left with a cursor that can never land.
   */
  const castSpell = useCallback((heroId: HeroId, spell: SpellDefId): void => {
    const s = session.getState();
    const hero = s.heroes[heroId];
    const content = session.getContent();
    if (hero === undefined) return;
    const plan = adventureTargeting(makeCtx(s, content), heroId, spell);

    if (plan.shape === "self" || plan.shape === "none") {
      const cmd: GameCommand = {
        type: "CAST_ADVENTURE_SPELL", hero: heroId, spell, x: hero.x, y: hero.y, z: hero.z,
      };
      const reason = session.check(cmd);
      if (reason !== null) { setNotice({ at: session.getVersion(), reason }); return; }
      // Call Vessel and the two travel spells do not move the hero, but Town
      // Portal at basic mastery does and arrives here as a "town" shape, so the
      // camera is told about every cast rather than about some of them.
      noteOrder(heroId);
      app.dispatch(cmd);
      app.closeOverlay();
      return;
    }

    if (plan.reason !== null || plan.cells.length === 0) {
      setNotice({
        at: session.getVersion(),
        reason: plan.reason ?? { code: "TARGET_INVALID" },
      });
      return;
    }

    const dims = { width: s.map.width, height: s.map.height, levels: s.map.levels };
    const cells = new Set<number>();
    for (const [x, y, z] of plan.cells) cells.add(cellIndexOf(dims, x, y, z));
    setAiming({
      hero: heroId,
      spell,
      name: content.spells.get(spell)?.name ?? (spell as string),
      shape: plan.shape,
      cells,
      range: plan.range,
      x: hero.x, y: hero.y, z: hero.z,
    });
    app.closeOverlay();
  }, [session, noteOrder]);

  /**
   * The click that lands the cast.
   *
   * A refused cell keeps the cursor armed and speaks the kernel's Reason: the
   * player mis-aimed, which is not a reason to throw his spell away, and a
   * click that changes nothing and says nothing is the failure this project
   * tracks by name.
   */
  const castAt = useCallback((x: number, y: number, z: number): void => {
    const aim = aimingRef.current;
    if (aim === null) return;
    const cmd: GameCommand = {
      type: "CAST_ADVENTURE_SPELL", hero: aim.hero, spell: aim.spell, x, y, z,
    };
    const reason = session.check(cmd);
    if (reason !== null) { setNotice({ at: session.getVersion(), reason }); return; }
    noteOrder(aim.hero);
    setAiming(null);
    app.dispatch(cmd);
  }, [session, noteOrder]);

  /**
   * The market rate, from the kernel and never from here.
   *
   * `tradeYield` is the function `TRADE_RESOURCE` actually pays out with, so
   * the number the Marketplace screen quotes is the number the treasury will
   * move by - including the part a player cannot see, that the spread narrows
   * with every Marketplace the kingdom owns. Deriving it in the screen from
   * `resourceValue` would be a second implementation of a rule (I10).
   */
  const tradeRate = useCallback(
    (give: ResourceKind, take: ResourceKind, amount: number): number => {
      const self = session.getObservation().self;
      if (self === null) return 0;
      const c = makeCtx(session.getState(), session.getContent());
      return tradeYield(c, self.id, give, take, amount);
    },
    [session, version],
  );

  const tradeReason = useCallback(
    (give: ResourceKind, take: ResourceKind, amount: number): Reason | null => {
      const self = session.getObservation().self;
      if (self === null) return null;
      return session.check({ type: "TRADE_RESOURCE", player: self.id, give, take, amount });
    },
    [session, version],
  );

  /**
   * A click on your own town on the map is a request to WALK IN.
   *
   * This is the reported bug: the town overlay opened over a hero who was still
   * standing in the field, so the garrison he was looking at was not one he
   * could reach and `HERO_TO_TOWN` - which the kernel implements and tests -
   * had no caller anywhere in the app.
   *
   * `heroToTown` is legal only from a cell touching the town (it is a step
   * through the gate, not a teleport), so a hero further off is walked there
   * first. Stepping onto the town's own cell already makes him the visiting
   * hero inside `moveHero`, which is why that path needs no follow-up; when the
   * entrance is not pathable the entry is queued behind a walk to a cell beside
   * it, and the human controller's queue keeps the two in order.
   */
  const enterTown = useCallback((id: TownId): void => {
    const s = session.getState();
    const town = s.towns[id];
    const heroId = selectionRef.current.heroId;
    const hero = heroId === null ? null : s.heroes[heroId] ?? null;
    if (town === undefined || heroId === null || hero === null || hero.inTown === id) {
      app.setOverlay({ kind: "town", town: id });
      return;
    }

    const enter: GameCommand = { type: "HERO_TO_TOWN", hero: heroId, town: id };
    const reason = session.check(enter);
    if (reason === null) {
      app.dispatch(enter);
      app.setOverlay({ kind: "town", town: id });
      return;
    }

    if (reason.code === "NOT_ADJACENT") {
      const onto = session.pathFor(heroId, town.x, town.y, town.z);
      if (onto.length > 0) { moveHero(heroId, onto); return; }
      const near = approachPath(session, heroId, town.x, town.y, town.z);
      if (near.length > 0) {
        moveHero(heroId, near);
        app.dispatch(enter);
        return;
      }
    }

    // Refused for something walking cannot fix - another hero already has the
    // visiting slot, it is not your turn, the town is not yours. Say so, and
    // still open the screen: looking at your own town is never illegal.
    setNotice({ at: session.getVersion(), reason });
    app.setOverlay({ kind: "town", town: id });
  }, [session, moveHero]);

  /**
   * A click on a town the hero has walked all the way up to, or cannot walk to
   * at all. `WorldCanvas` resolves which town was clicked and hands it over
   * without caring whose it is; deciding what that means is this shell's job,
   * and answering it is the kernel's.
   *
   * Your own town is the gesture that already existed. Anyone else's is the
   * one a player expects to be an assault, and the honest answer today is that
   * it is refused: no rule in `packages/core/src/adventure` can take a town
   * from another player, because nothing starts a siege - `startObjectBattle`
   * needs an object with a guard, and a town is neither. So the command is put
   * to the kernel with `check`, exactly as the rest of this shell does (issuing
   * a command the UI knows will be refused is a legality bug), and the kernel's
   * own Reason goes on the status bar. When the kernel grows a siege this is
   * the gesture that will start it and this reading does not change; until
   * then the player is told "You do not control town:2" instead of being told
   * nothing at all, which is what a hero standing at the enemy's open gate for
   * thirty five turns was told before.
   */
  const reachTown = useCallback((id: TownId): void => {
    const s = session.getState();
    const town = s.towns[id];
    if (town === undefined) return;
    if (town.owner === session.getViewer()) { enterTown(id); return; }
    const heroId = selectionRef.current.heroId;
    if (heroId === null) return;
    const enter: GameCommand = { type: "HERO_TO_TOWN", hero: heroId, town: id };
    const reason = session.check(enter);
    if (reason === null) { app.dispatch(enter); return; }
    setNotice({ at: session.getVersion(), reason });
  }, [session, enterTown]);

  /**
   * Two of the player's heroes meeting: the classic's exchange window.
   *
   * The sheet shown is the MOVER's - the hero whose click this was - with the
   * other hero as the partner, which is the way round the classic puts them and the way
   * round the give buttons read ("give to him", not "take from him"). SWITCH on
   * the sheet turns it around.
   */
  const meetHero = useCallback((mover: HeroId, other: HeroId): void => {
    app.setOverlay({ kind: "hero", hero: mover, with: other });
  }, []);

  // The adventure shell binds space / e / c / arrows itself. These three are the
  // screens a player reaches constantly and that the sidebar has no room for.
  /**
   * Why the selected hero may not dig, or null.
   *
   * Asked of the kernel through `check`, like every other refusal on this
   * screen - the shell may not derive one (I10) - and memoised on the version
   * the session is at, because the command block re-renders on every hover.
   */
  const digReason = useMemo<Reason | null>(() => {
    const id = selection.heroId;
    if (id === null) return { code: "NO_SUCH_ENTITY", id: "hero" };
    return session.check({ type: "DIG", hero: id });
  }, [session, selection.heroId, version]);

  const extraHotkeys = useMemo(() => [
    {
      combo: "h",
      description: "Open the selected hero's sheet",
      onFire: () => {
        const id = selectionRef.current.heroId;
        if (id !== null) app.setOverlay({ kind: "hero", hero: id });
      },
    },
    {
      combo: "t",
      description: "Open the selected town",
      onFire: () => {
        const id = selectionRef.current.townId;
        if (id !== null) app.setOverlay({ kind: "town", town: id });
      },
    },
    {
      // The other half of the world. The map is two planes joined by paired
      // subterranean gates, and until this round nothing in the UI could
      // change `camera.z` at all: the cavern was generated, guarded, stocked
      // with its own objects and never drawn. The key is the keyboard half of
      // the plates under the minimap and goes through the same arithmetic.
      combo: "u",
      description: "Surface / underground",
      onFire: () => {
        setCamera((c) => toggleLevel(c, mapRef.current));
      },
    },
    {
      combo: "-",
      description: "Zoom the map out: see more of it, at three quarters then half size",
      onFire: () => { setMapZoom((z) => stepMapZoom(z, -1)); },
    },
    {
      combo: "=",
      description: "Zoom the map in (+ does the same)",
      onFire: () => { setMapZoom((z) => stepMapZoom(z, 1)); },
    },
    { combo: "k", description: "Kingdom overview", onFire: () => { app.setOverlay({ kind: "kingdom" }); } },
    {
      combo: "j",
      description: "Journal: what you were asked, and what has happened",
      onFire: () => { app.setOverlay({ kind: "journal" }); },
    },
    {
      // Digging costs the hero his whole day and finds nothing on all but one
      // cell of the map, so the key is disabled with the kernel's own reason
      // rather than firing a command the kernel will refuse. That is the same
      // contract every other control on this screen keeps: ask first, and print
      // the refusal instead of swallowing it.
      combo: "d",
      description: digReason === null
        ? "Dig here for the Grail - it costs the whole day"
        : `Cannot dig: ${reasonText(digReason)}`,
      disabled: digReason !== null,
      onFire: () => {
        const id = selectionRef.current.heroId;
        if (id !== null) app.dispatch({ type: "DIG", hero: id });
      },
    },
    {
      // The obelisks' own picture of where the Grail is. Always available -
      // there is nothing to refuse, and a player with no obelisks visited yet
      // is told that by the screen rather than by a dead key.
      combo: "p",
      description: "Puzzle Map: what the obelisks have uncovered",
      onFire: () => { app.setOverlay({ kind: "puzzle" }); },
    },
    {
      // Escape means "get me out of whatever I am in", and an armed spell
      // cursor is the innermost of those: it captures every click on the map,
      // so it has to be the first thing Escape gives back.
      combo: "escape",
      description: "Cancel the spell cursor, or close the open screen",
      onFire: () => {
        if (aimingRef.current !== null) { setAiming(null); return; }
        app.closeOverlay();
      },
    },
  ], [digReason]);

  // ---- overlays
  const overlay = snapshot.overlay;
  // Only a battle the viewer is in. A computer player clearing a wandering
  // monster on the far side of the map is not the player's business, and
  // showing it would both spoil the fog and drag a 112 MB atlas onto the GPU
  // for a fight nobody asked to watch.
  const battle = state.battle !== null && sideOf(session) !== null ? state.battle : null;

  /**
   * The battle the SCREEN is showing, which outlives the one the kernel is
   * running by exactly one animation.
   *
   * `resolveBattleOutcome` sets `state.battle = null` inside the SAME command
   * that lands the last blow, so the kernel's own account of a fight ends one
   * frame after the killing stroke is thrown. Left alone, that is what the
   * player sees: against a wandering monster - most of the fights in the first
   * week - the whole of the last exchange is the shot, the death and the end of
   * the battle in one batch, and the screen carrying them vanishes before any
   * of it can be drawn. The decisive blow of the fight was the one blow with no
   * picture.
   *
   * `executeBattleCommand` mutates its `BattleState` in place, so the object
   * held from before the command IS the final state; nothing here reconstructs
   * anything. `present` parks it here while it plays the last batch out and
   * clears it when the animation finishes, which is also what holds the
   * after-action report back until the last creature is down.
   */
  const [closing, setClosing] = useState<BattleState | null>(null);
  const shownBattle = battle ?? closing;
  const lastBattle = useRef<BattleState | null>(null);
  lastBattle.current = battle;
  /**
   * The frame the closing screen is frozen on.
   *
   * `version` and `side` are held at their last live values so that the props
   * `BattleScreen` derives its `BattleView` from do not move while the last
   * animation plays: `BattlePresenter.update` compares version, state identity
   * and `ui` by reference and returns without redrawing when none of them
   * changed, so the whole of the closing second is the animator's and nothing
   * else touches the scene. The board a battle ends on is not a board any rule
   * still applies to - `sideOf` is already null by then and the console would
   * rebuild itself around a fight nobody can act in - and freezing is both the
   * cheaper answer and the one that cannot go wrong.
   */
  const frozen = useRef<{ readonly version: number; readonly side: 0 | 1 | null }>({ version: 0, side: null });
  if (battle !== null) frozen.current = { version, side: sideOf(session) };
  const shownVersion = battle !== null ? version : frozen.current.version;
  const shownSide = battle !== null ? sideOf(session) : frozen.current.side;
  useEffect(() => {
    // A new fight supersedes the one still playing itself out.
    if (battle !== null && closing !== null) setClosing(null);
  }, [battle, closing]);

  /**
   * Who is actually commanding each army in the running battle.
   *
   * The console cannot work this out for itself and must not guess. A
   * `BattleHeroState` is `{id, owner, attack, defense, spellPower, knowledge,
   * mana, spellbook, castThisRound, army}` - a runtime `HeroId` like `hero:1`
   * with no def id - so `@game/render` has no way to reach a name or a
   * portrait, and its fallback picks a hero of the right faction and the right
   * class from the roster. That is a stand-in, and in a real game it prints a
   * real hero who is not this one, which is worse than printing nothing.
   *
   * This file is the party that CAN resolve it: it holds the `GameState` the
   * `HeroId` indexes and already resolves the same id to a name and a portrait
   * key for the after-action report, so the two screens now agree by
   * construction. A side with no hero at all - a garrison, a guard, a wandering
   * pack - is named after whatever owns the army, exactly as `openTrack` names
   * it, rather than being left null and falling back to the stand-in.
   */
  const commanders = useMemo((): readonly [
    { readonly name: string; readonly portrait: string } | null,
    { readonly name: string; readonly portrait: string } | null,
  ] | undefined => {
    if (shownBattle === null || content === null) return undefined;
    const of = (s: 0 | 1): { readonly name: string; readonly portrait: string } => {
      const id = shownBattle.heroes[s].id;
      return {
        name: heroNameOf(state, content, id)
          ?? armyOwnerName(state, content, shownBattle.heroes[s].army),
        // "" rather than null: the console tests the key against the atlas and
        // draws the plaque with no face when it is not there.
        portrait: heroPortraitOf(state, content, id) ?? "",
      };
    };
    return [of(SIDE_ATTACKER), of(SIDE_DEFENDER)];
  }, [shownBattle, version, content, state]);

  // The kernel parks the game on a pending interaction and accepts nothing else
  // until it is answered, so this outranks every other overlay.
  const pending = state.pending !== null && state.pending.playerId === session.getViewer()
    ? state.pending
    : null;

  // ---- the end of the game
  //
  // `evaluateVictory` decides this and `TurnLoop` stops on it; nothing rendered
  // it, so a decided game showed as a map that had quietly stopped answering.
  // Both halves are read here, and the second one matters: with three players a
  // human can be `defeated` while the game itself runs on, and that player must
  // be told he has lost rather than left holding a board he cannot move.
  const ending = useMemo<Ending | null>(
    () => endingFor(state, obs, content),
    [state, obs, content, version],
  );

  const toMenu = useCallback((): void => { app.reset(); app.go({ kind: "menu" }); }, []);
  const toNewGame = useCallback((): void => { app.reset(); app.go({ kind: "newgame" }); }, []);

  const canvas = (
    <WorldCanvas
      session={session}
      presenterRef={presenterRef}
      camera={camera}
      zoom={mapZoom}
      onCameraChange={panCamera}
      selection={selection}
      onSelect={setSelection}
      onHover={setHover}
      onOpenTown={enterTown}
      onVisitObject={visitObject}
      onReachTown={reachTown}
      onMeetHero={meetHero}
      onCommand={(cmd) => { moveHero(cmd.hero, cmd.path); }}
      aiming={aiming}
      onAimAt={castAt}
      onAimCancel={cancelAim}
      onInfoAt={showInfo}
      onInfoDismiss={hideInfo}
      onPendingTap={setPendingCell}
      onZoom={(dir) => { setMapZoom((z) => stepMapZoom(z, dir)); }}
      version={version}
      interactive={snapshot.actorKind === "human" && shownBattle === null}
    />
  );

  return (
    <GameStoreContext.Provider value={store}>
      <AtlasContext.Provider value={props.uiAtlas}>
        <div className="hm-app">
          <AdventureScreen
            canvas={canvas}
            camera={camera}
            onCameraChange={panCamera}
            selection={selection}
            onSelectionChange={setSelection}
            status={status}
            onHover={setHover}
            onEndTurn={endTurn}
            endTurnReason={endTurnReason}
            onOpenMenu={props.onOpenMenu}
            onOpenTown={openTown}
            onOpenHero={(id) => { app.setOverlay({ kind: "hero", hero: id }); }}
            capacities={capacities}
            conditionSubject={conditionSubject(state, content, state.victory.condition)}
            extraHotkeys={extraHotkeys}
            scale={props.uiScale}
            onInfoKey={infoAtArmed}
            infoOpen={infoOpen}
          />

          {snapshot.actorKind === "ai" && shownBattle === null ? (
            <div className="hm-thinking">The enemy is moving</div>
          ) : null}

          {/* The right-press card. Drawn last of the map layers so it is over
              the panels as well as the map, and outside the battle branch
              because a battle screen has its own right-click rules. */}
          {infoCard !== null ? (
            <InfoPopup card={infoCard.card} x={infoCard.x} y={infoCard.y} />
          ) : null}

          {/* The armed cursor has to be visible: a map that answers every click
              with a spell and says nowhere that it is doing so is a mode the
              player has fallen into rather than one he is in. */}
          {aiming !== null ? (
            <div
              className="hm-aim"
              role="status"
              style={{
                position: "absolute", top: "10px", left: "50%", transform: "translateX(-50%)",
                zIndex: 30, padding: "6px 14px", borderRadius: "4px", pointerEvents: "auto",
                background: "rgba(12,16,22,0.88)", border: "1px solid #d9b25a",
                color: "#f2e4c1", font: "600 13px/1.4 system-ui, sans-serif",
                display: "flex", gap: "10px", alignItems: "center",
              }}
            >
              <span>{aimingPrompt(aiming)}</span>
              <button
                type="button"
                onClick={cancelAim}
                style={{
                  font: "inherit", color: "inherit", background: "transparent",
                  border: "1px solid #6c5a34", borderRadius: "3px", padding: "1px 8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          ) : null}

          {shownBattle !== null && props.pixiAtlas !== null && content !== null ? (
            <div className="hm-overlay">
              <BattleScreen
                atlas={props.pixiAtlas}
                content={content}
                state={shownBattle}
                version={shownVersion}
                events={battleEvents}
                commanders={commanders}
                side={shownSide}
                animations={props.animations}
                presenterRef={battlePresenterRef}
                onCommand={battleCommand}
              />
            </div>
          ) : null}

          {pending !== null && content !== null ? (
            <PromptDialog
              pending={pending}
              content={content}
              onRespond={(response) => {
                // Answering can move him: the prompt a monolith with several
                // exits raises is answered here and the hero comes out of one
                // of them, possibly on the other level. Recorded so the camera
                // follows, exactly as it follows a walk.
                const hero = selectionRef.current.heroId;
                if (hero !== null) noteOrder(hero);
                app.dispatch({ type: "RESPOND_TO_INTERACTION", id: pending.id, response });
              }}
            />
          ) : null}

          {shownBattle === null && overlay.kind !== "none" && content !== null ? (
            <div className="hm-overlay">
              <Overlays
                session={session}
                overlay={overlay}
                tradeRate={tradeRate}
                tradeReason={tradeReason}
                result={resultView.current}
                onCloseResult={closeResult}
                onCastSpell={castSpell}
                selectedHeroId={selection.heroId}
                onGoTo={goTo}
              />
            </div>
          ) : null}

          {/* Over everything, including a screen the player left open: the game
              has stopped, and no panel behind this one can still be used. The
              one thing it waits for is the after-action report, because the
              fight that ended the game is the one the player most wants to
              read - dismissing the report reveals this. */}
          {ending !== null && overlay.kind !== "result" ? (
            <div className="hm-overlay" style={{ zIndex: 40 }}>
              <GameOver
                outcome={ending.outcome}
                winnerName={ending.winnerName}
                condition={ending.condition}
                conditionSubject={ending.subject}
                day={state.turn.day}
                week={state.turn.week}
                month={state.turn.month}
                standing={ending.standing}
                eliminated={ending.eliminated}
                onMainMenu={toMenu}
                onNewGame={toNewGame}
              />
            </div>
          ) : null}
        </div>
      </AtlasContext.Provider>
    </GameStoreContext.Provider>
  );
}

/**
 * Is a cell comfortably on screen?
 *
 * A one-cell margin, because a hero standing in the outermost visible column is
 * half under the panel edge and half of his next step is off screen: "visible"
 * has to mean "visible enough to play from" or the camera never helps.
 */
function cellInView(c: CameraView, x: number, y: number, z: number): boolean {
  if (z !== c.z) return false;
  const m = c.widthCells > 4 && c.heightCells > 4 ? 1 : 0;
  return x >= c.x + m && x < c.x + c.widthCells - m
    && y >= c.y + m && y < c.y + c.heightCells - m;
}

/**
 * The cheapest walk to a cell touching (x,y,z), for a hero who wants in and is
 * not there yet. Every candidate route comes from the session's own Dijkstra
 * pass, so this ranks the kernel's answers and computes none of its own.
 */
function approachPath(
  session: GameSession, hero: HeroId, x: number, y: number, z: number,
): readonly number[] {
  const obs = session.getObservation();
  let best: readonly number[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const cx = x + dx, cy = y + dy;
      if (cx < 0 || cy < 0 || cx >= obs.map.width || cy >= obs.map.height) continue;
      const path = session.pathFor(hero, cx, cy, z);
      if (path.length === 0) continue;
      if (best.length === 0 || path.length < best.length) best = path;
    }
  }
  return best;
}

/**
 * What a map object is selling, in the kernel's own numbers.
 *
 * `BUY_ARTIFACT` reads the object's payload for the artifact and for `price`,
 * falling back to the artifact's own `cost` when the object names none
 * (`buyArtifact`, adventure/execute.ts). Both halves are the kernel's, so the
 * host resolves the offer with the kernel's own `payloadOf` and hands the
 * screen a finished list: a price computed in the UI would be a second
 * implementation of the rule and would eventually quote a number the kernel
 * refuses to honour.
 *
 * The kernel offers at most one artifact per object, so this list is empty or a
 * single row; it is a list because the shape of a shop is a list, and because
 * an object that ever carries two would then need no new screen.
 */
function merchantOffers(
  state: GameState, content: ContentRegistry, id: ObjectId,
): readonly ArtifactOffer[] {
  const o = state.objects[id];
  // An object that IS an artifact lying on the ground is picked up, not bought.
  if (o === undefined || o.removed || o.kind === "artifact") return [];
  const pay = payloadOf(makeCtx(state, content), o);
  const artifact = typeof pay["artifact"] === "string" ? pay["artifact"] : "";
  if (artifact === "") return [];
  const def = content.artifacts.get(artifact as ArtifactDefId);
  if (def === undefined) return [];
  const priced = pay["price"];
  return [{
    artifact: artifact as ArtifactDefId,
    price: typeof priced === "number" ? priced : def.cost,
  }];
}

const EMPTY_RESOURCES = Object.freeze({
  gold: 0, wood: 0, ore: 0, mercury: 0, sulfur: 0, crystal: 0, gems: 0,
});

/** "bld_black_market" -> "Black Market". Map-object defs carry no display name,
 *  so the id is all there is to put over the counter. */
function shopName(id: string): string {
  return titleCase(id.replace(/^(bld|tp|pu|obj)_/, ""));
}

/** Which side of the running battle the viewer is on. */
function sideOf(session: GameSession): 0 | 1 | null {
  const b = session.getState().battle;
  if (b === null) return null;
  const me = session.getViewer();
  if (b.heroes[SIDE_ATTACKER].owner === me) return SIDE_ATTACKER;
  if (b.heroes[SIDE_DEFENDER].owner === me) return SIDE_DEFENDER;
  return null;
}

// ----------------------------------------------------- the after-action report

/** What the shell holds while a battle the viewer is in is running. */
interface BattleTrack {
  readonly viewerSide: 0 | 1;
  readonly armies: readonly [ArmyId | null, ArmyId | null];
  /** The two armies as they stood when the battle opened. */
  readonly started: readonly [readonly (ArmySlot | null)[], readonly (ArmySlot | null)[]];
  readonly heroes: readonly [HeroId | null, HeroId | null];
  readonly names: readonly [string, string];
  /** Portraits by hero id, captured while both heroes still exist: the loser is
   *  removed from state by `retireHero` before the report is built. */
  readonly portraits: Readonly<Record<string, string | null>>;
  rounds: number;
  readonly events: GameEvent[];
}

/** The half of the report that is presentation and so cannot live in the
 *  `Overlay` union, which carries the `BattleReport` and nothing else. */
interface ResultView {
  readonly viewerSide: 0 | 1;
  readonly attackerName: string;
  readonly defenderName: string;
  readonly portraits: Readonly<Record<string, string | null>>;
}

/** One finished fight waiting behind the report on screen, oldest first. */
interface QueuedReport {
  readonly report: BattleReport;
  readonly view: ResultView;
}

function armySlots(state: GameState, id: ArmyId | null): readonly (ArmySlot | null)[] {
  if (id === null) return [];
  return state.armies[id]?.slots ?? [];
}

function heroNameOf(state: GameState, content: ContentRegistry, id: HeroId | null): string | null {
  if (id === null) return null;
  const h = state.heroes[id];
  if (h === undefined) return null;
  return content.heroes.get(h.def)?.name ?? (h.def as string);
}

function heroPortraitOf(state: GameState, content: ContentRegistry, id: HeroId | null): string | null {
  if (id === null) return null;
  const h = state.heroes[id];
  if (h === undefined) return null;
  const def = content.heroes.get(h.def);
  return def === undefined ? null : heroPortrait(def.portrait);
}

/**
 * Who the defending side is, when it has no hero.
 *
 * A garrison and a guard are both "an army with no hero" to the battle kernel,
 * and "Defender" over a casualty list tells a player nothing. The town or the
 * object that owns the army is a plain state read, so naming it here decides no
 * rule and is worth the twenty lines.
 */
function armyOwnerName(state: GameState, content: ContentRegistry, id: ArmyId | null): string {
  if (id === null) return "Defenders";
  for (const t of entityKeys(state.towns)) {
    const town = state.towns[t as TownId];
    if (town !== undefined && town.garrison === id) return town.name;
  }
  for (const o of entityKeys(state.objects)) {
    const obj = state.objects[o as ObjectId];
    if (obj === undefined || obj.guard !== id) continue;
    if (obj.kind === "monster") return "Wandering Monsters";
    return shopName(content.mapObjects.get(obj.def)?.id ?? obj.def);
  }
  return "Defenders";
}

function openTrack(
  state: GameState, content: ContentRegistry, viewerSide: 0 | 1, events: readonly GameEvent[],
): BattleTrack {
  const b = state.battle!;
  const armies: readonly [ArmyId | null, ArmyId | null] = [
    b.heroes[SIDE_ATTACKER].army, b.heroes[SIDE_DEFENDER].army,
  ];
  const heroes: readonly [HeroId | null, HeroId | null] = [
    b.heroes[SIDE_ATTACKER].id, b.heroes[SIDE_DEFENDER].id,
  ];
  const portraits: Record<string, string | null> = {};
  for (const h of heroes) {
    if (h !== null) portraits[h as string] = heroPortraitOf(state, content, h);
  }
  return {
    viewerSide,
    armies,
    started: [armySlots(state, armies[0]), armySlots(state, armies[1])],
    heroes,
    names: [
      heroNameOf(state, content, heroes[0]) ?? armyOwnerName(state, content, armies[0]),
      heroNameOf(state, content, heroes[1]) ?? armyOwnerName(state, content, armies[1]),
    ],
    portraits,
    rounds: b.round,
    events: [...events],
  };
}

/** Started against survived, merged by creature. Counting, not deciding. */
function sideFrom(
  content: ContentRegistry,
  started: readonly (ArmySlot | null)[],
  ended: readonly (ArmySlot | null)[],
  side: 0 | 1,
  hero: HeroId | null,
): SideReport {
  const merged = new Map<string, { started: number; survived: number }>();
  const order: CreatureDefId[] = [];
  const row = (def: CreatureDefId): { started: number; survived: number } => {
    const hit = merged.get(def as string);
    if (hit !== undefined) return hit;
    const made = { started: 0, survived: 0 };
    merged.set(def as string, made);
    order.push(def);
    return made;
  };
  for (const s of started) if (s !== null) row(s.def).started += s.count;
  for (const s of ended) if (s !== null) row(s.def).survived += s.count;

  const lines: CasualtyLine[] = [];
  let startedTotal = 0;
  let survivedTotal = 0;
  let valueLost = 0;
  for (const def of order) {
    const r = merged.get(def as string)!;
    const cdef = content.creatures.get(def);
    // Gravecalling raises into the winner's army after the fold, so survivors can
    // exceed the starting count. That is a gain, never a negative casualty.
    const lost = r.started > r.survived ? r.started - r.survived : 0;
    lines.push({
      creature: def,
      name: cdef?.namePlural ?? (def as string),
      started: r.started,
      survived: r.survived,
      lost,
    });
    startedTotal += r.started;
    survivedTotal += r.survived;
    valueLost += lost * (cdef?.aiValue ?? 0);
  }

  return {
    side,
    hero,
    lines,
    startedTotal,
    survivedTotal,
    lostTotal: startedTotal > survivedTotal ? startedTotal - survivedTotal : 0,
    valueLost,
  };
}

function reportFrom(content: ContentRegistry, state: GameState, track: BattleTrack): BattleReport {
  const experience: { hero: HeroId; amount: number }[] = [];
  const artifacts: { hero: HeroId; artifact: ArtifactDefId }[] = [];
  const levelUps: { hero: HeroId; level: number }[] = [];
  let winner: 0 | 1 | null = null;
  let kind = "ongoing";
  for (const e of track.events) {
    if (e.type === "HeroGainedExperience") experience.push({ hero: e.hero, amount: e.amount });
    else if (e.type === "ArtifactGained") artifacts.push({ hero: e.hero, artifact: e.artifact });
    else if (e.type === "HeroLeveledUp") levelUps.push({ hero: e.hero, level: e.level });
    else if (e.type === "BattleEnded") { winner = e.winner; kind = e.kind; }
  }
  return {
    winner,
    kind,
    rounds: track.rounds,
    attacker: sideFrom(content, track.started[0], armySlots(state, track.armies[0]), 0, track.heroes[0]),
    defender: sideFrom(content, track.started[1], armySlots(state, track.armies[1]), 1, track.heroes[1]),
    experience,
    artifacts,
    levelUps,
  };
}

// ------------------------------------------------------------ the end of it

interface Ending {
  readonly outcome: GameOutcome;
  readonly winnerName: string | null;
  readonly condition: string;
  readonly subject: string | null;
  readonly standing: readonly StandingRow[];
  readonly eliminated: boolean;
}

/** A player's name. The kernel has none, and a colour is what the classic calls a
 *  player, so the colour is the name. */
function playerName(colour: string, id: PlayerId): string {
  return colour === "" || colour.charCodeAt(0) === 35 ? (id as string) : titleCase(colour);
}

/** Whatever the win condition names, in words a player recognises. */
function conditionSubject(state: GameState, content: ContentRegistry | null, condition: string): string | null {
  const sep = condition.indexOf(":");
  if (sep < 0) return null;
  const kind = condition.slice(0, sep);
  const arg = condition.slice(sep + 1);
  if (kind === "capture_town") return state.towns[arg as TownId]?.name ?? null;
  if (kind === "defeat_hero") return content === null ? null : heroNameOf(state, content, arg as HeroId);
  if (kind === "acquire_artifact") {
    return content?.artifacts.get(arg as ArtifactDefId)?.name ?? null;
  }
  return null;
}

/**
 * Is this game over for the viewer, and how?
 *
 * Two separate facts. `victory.over` ends the game for everyone; `self.defeated`
 * ends it only for this player, and with three or more players the loop keeps
 * running around him. Either one earns the screen.
 */
function endingFor(
  state: GameState, obs: PlayerObservation, content: ContentRegistry | null,
): Ending | null {
  const self = obs.self;
  if (self === null) return null;
  const v = state.victory;
  if (!v.over && !self.defeated) return null;

  const outcome: GameOutcome = v.winner === self.id ? "victory"
    : v.winner === null && !self.defeated ? "draw"
      : "defeat";

  const standing: StandingRow[] = [{
    name: playerName(self.colour, self.id),
    colour: self.colour,
    towns: self.towns.length,
    heroes: self.heroes.length,
    defeated: self.defeated,
    winner: v.winner === self.id,
    you: true,
  }];
  for (const o of obs.opponents) {
    if (o.kind === "none") continue;
    standing.push({
      name: playerName(o.colour, o.id),
      colour: o.colour,
      towns: o.townCount,
      heroes: o.heroCount,
      defeated: o.defeated,
      winner: v.winner === o.id,
      you: false,
    });
  }

  const winnerRow = standing.find((r) => r.winner) ?? null;
  return {
    outcome,
    winnerName: winnerRow === null ? null : winnerRow.name,
    condition: v.condition,
    subject: content === null ? null : conditionSubject(state, content, v.condition),
    standing,
    eliminated: self.defeated,
  };
}

// -------------------------------------------------------------- the overlays

function Overlays(props: {
  readonly session: GameSession;
  readonly overlay: Exclude<Overlay, { kind: "none" }>;
  readonly tradeRate: (give: ResourceKind, take: ResourceKind, amount: number) => number;
  readonly tradeReason: (give: ResourceKind, take: ResourceKind, amount: number) => Reason | null;
  /** Names and portraits for the after-action report; see `ResultView`. */
  readonly result: ResultView | null;
  /** Dismiss the result report and show the next queued one, if any. */
  readonly onCloseResult: () => void;
  /** The shell's cast path: casts a self-aimed spell, arms a cursor for the
   *  three that take a target. */
  readonly onCastSpell: (hero: HeroId, spell: SpellDefId) => void;
  /** Whose sheet the journal asks "could this hero pay it", or null. */
  readonly selectedHeroId: HeroId | null;
  /** Centre the map on a cell, for the journal's task list. */
  readonly onGoTo: (x: number, y: number, z: number) => void;
}): JSX.Element | null {
  const { session, overlay } = props;
  const content = session.getContent();
  const state = session.getState();
  const close = useCallback(() => { app.closeOverlay(); }, []);

  if (overlay.kind === "town") {
    const town: TownState | undefined = state.towns[overlay.town];
    if (town === undefined) return null;
    const garrison: ArmyState | null = state.armies[town.garrison] ?? null;
    const visiting: HeroState | null = town.visitingHero === null ? null : (state.heroes[town.visitingHero] ?? null);
    const visitingArmy: ArmyState | null = visiting === null ? null : (state.armies[visiting.army] ?? null);
    const player = state.players[session.getViewer()];
    // Read-only: `availableOf` and `townUpgradeOffers` take a Ctx for its
    // content and armies and write nothing, the same shape `maxMana` takes
    // for the hero sheet below.
    const ctx = makeCtx(state, content!);
    return (
      <TownScreen
        content={content!}
        town={town}
        treasury={player?.resources ?? { gold: 0, wood: 0, ore: 0, mercury: 0, sulfur: 0, crystal: 0, gems: 0 }}
        garrison={garrison}
        visitingHero={visiting}
        visitingArmy={visitingArmy}
        day={state.turn.day}
        tavernOffers={player?.tavern ?? []}
        buildReason={(b: BuildingDefId) => session.check({ type: "BUILD", town: town.id, building: b })}
        recruitReason={(c: CreatureDefId, count: number) =>
          session.check({ type: "RECRUIT", town: town.id, creature: c, count })}
        available={(c) => availableOf(ctx, town, c)}
        upgradeOffers={(a) => townUpgradeOffers(ctx, town, a)}
        hireReason={(h: HeroDefId) => session.check({ type: "HIRE_HERO", town: town.id, hero: h })}
        // Computed when the screen mounts rather than per render: it walks
        // every owned dwelling and every other town, and asks the kernel for
        // each. Cheap on a kingdom, not free.
        caravanOffers={session.caravanOffers(town.id)}
        caravansOnTheRoad={session.caravansOnTheRoad()}
        tradeRate={props.tradeRate}
        tradeReason={props.tradeReason}
        checkCommand={(cmd: GameCommand) => session.check(cmd)}
        ownerColour={town.owner === null ? null : (state.players[town.owner]?.colour ?? null)}
        onCommand={(cmd) => { app.dispatch(cmd); }}
        onClose={close}
        onOpenHero={(hero) => { app.setOverlay({ kind: "hero", hero: hero.id }); }}
      />
    );
  }

  if (overlay.kind === "hero") {
    const hero = state.heroes[overlay.hero];
    if (hero === undefined) return null;
    const ctx = makeCtx(state, content!);
    /*
     * The second half of a meeting. It is re-resolved on every render rather
     * than captured when the overlay opened, because the meeting can END while
     * the window is up: hand a hero his last stack away and the kernel refuses,
     * but walk him off with a queued move and the exchange must stop offering
     * trades that the kernel would now refuse. Dropping to `null` here takes
     * the other army row and the give buttons away with it.
     */
    const partner = overlay.with === undefined ? null : state.heroes[overlay.with] ?? null;
    const meeting = partner !== null
      && partner.id !== hero.id
      && partner.owner === hero.owner
      && partner.z === hero.z
      && Math.abs(partner.x - hero.x) <= 1
      && Math.abs(partner.y - hero.y) <= 1
      ? partner
      : null;
    const partnerDef = meeting === null ? null : content!.heroes.get(meeting.def) ?? null;
    return (
      <HeroScreen
        content={content!}
        hero={hero}
        primary={primaryFor(ctx, hero)}
        manaMax={maxMana(ctx, hero)}
        army={state.armies[hero.army] ?? null}
        otherArmy={meeting === null ? null : state.armies[meeting.army] ?? null}
        otherLabel={meeting === null ? undefined
          : `${partnerDef?.name ?? (meeting.def as string)}'s army`}
        otherHero={meeting}
        giveReason={meeting === null ? undefined : (index: number) =>
          session.check({
            type: "GIVE_ARTIFACT", fromHero: hero.id, toHero: meeting.id, backpackIndex: index,
          })}
        onSwapHeroes={meeting === null ? undefined
          : () => { app.setOverlay({ kind: "hero", hero: meeting.id, with: hero.id }); }}
        checkCommand={(cmd: GameCommand) => session.check(cmd)}
        equipReason={(index: number, slot: string) =>
          session.check({ type: "EQUIP_ARTIFACT", hero: hero.id, backpackIndex: index, slot })}
        unequipReason={(slot: string) =>
          session.check({ type: "UNEQUIP_ARTIFACT", hero: hero.id, slot })}
        ownerColour={state.players[hero.owner]?.colour ?? null}
        onCommand={(cmd) => { app.dispatch(cmd); }}
        onOpenSpellbook={() => { app.setOverlay({ kind: "spellbook", hero: hero.id }); }}
        onClose={close}
      />
    );
  }

  if (overlay.kind === "spellbook") {
    const hero = state.heroes[overlay.hero];
    if (hero === undefined) return null;
    /*
     * Whether the book greys a spell out cannot be "would casting it where the
     * hero stands be legal": that is exactly the question Blink Step and
     * Sink Vessel answer no to by rule, so the two spells with the most
     * interesting targets were the two the book showed as unusable. It asks
     * `adventureTargeting` instead, which answers the gate for a self-aimed
     * spell and "is there anything at all to aim at" for a targeted one.
     */
    const reasonFor = (spell: SpellDefId): Reason | null => {
      const plan = adventureTargeting(makeCtx(state, content!), hero.id, spell);
      if (plan.shape === "self" || plan.shape === "none") {
        return session.check({
          type: "CAST_ADVENTURE_SPELL", hero: hero.id, spell, x: hero.x, y: hero.y, z: hero.z,
        });
      }
      return plan.reason;
    };
    return (
      <SpellBook
        content={content!}
        hero={hero}
        castReason={reasonFor}
        onCast={(spell: SpellDefId) => { props.onCastSpell(hero.id, spell); }}
        onClose={close}
      />
    );
  }

  if (overlay.kind === "merchant") {
    const object = state.objects[overlay.object];
    const hero = state.heroes[overlay.hero];
    if (object === undefined || hero === undefined || content === null) return null;
    const def = content.mapObjects.get(object.def);
    return (
      <ArtifactMerchant
        content={content}
        object={object.id}
        title={shopName(def?.id ?? object.def)}
        hero={hero}
        treasury={state.players[session.getViewer()]?.resources ?? EMPTY_RESOURCES}
        offers={merchantOffers(state, content, object.id)}
        buyReason={(artifact: ArtifactDefId) =>
          session.check({ type: "BUY_ARTIFACT", hero: hero.id, object: object.id, artifact })}
        onCommand={(cmd) => { app.dispatch(cmd); }}
        onClose={close}
      />
    );
  }

  if (overlay.kind === "result") {
    if (content === null) return null;
    const view = props.result;
    return (
      <BattleResult
        content={content}
        report={overlay.report}
        viewerSide={view?.viewerSide ?? null}
        attackerName={view?.attackerName ?? "Attacker"}
        defenderName={view?.defenderName ?? "Defender"}
        heroPortrait={(hero: HeroId) => view?.portraits[hero as string] ?? null}
        onClose={props.onCloseResult}
      />
    );
  }

  if (overlay.kind === "journal") {
    const viewer = session.getViewer();
    return (
      <Journal
        content={content}
        condition={state.victory.condition}
        conditionSubject={conditionSubject(state, content, state.victory.condition)}
        quests={content === null
          ? []
          : journalQuests(state, content, viewer, props.selectedHeroId)}
        chronicle={content === null
          ? []
          : journalChronicle(session.eventLog(), state, content, viewer)}
        onGoTo={(x, y, z) => { props.onGoTo(x, y, z); close(); }}
        onClose={close}
      />
    );
  }

  if (overlay.kind === "puzzle") {
    return (
      <PuzzleMap
        view={session.puzzleView()}
        onGoTo={(x, y, z) => { props.onGoTo(x, y, z); close(); }}
        onClose={close}
      />
    );
  }

  if (overlay.kind === "kingdom") {
    return (
      <KingdomOverview
        content={content!}
        obs={session.getObservation()}
        onOpenTown={(t) => { app.setOverlay({ kind: "town", town: t.id }); }}
        onOpenHero={(h) => { app.setOverlay({ kind: "hero", hero: h.id }); }}
        onClose={close}
      />
    );
  }

  return null;
}

export type { HeroId };
